// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { SCENARIO_RUN_STATE } from '../../../../../../services/config/ApiConstants';
import WorkspaceService from '../../../../../../services/workspace/WorkspaceService';
import { useSetApplicationErrorMessage } from '../../../../../../state/hooks/ApplicationHooks';
import { useOrganizationId } from '../../../../../../state/hooks/OrganizationHooks';
import {
  useCurrentScenarioData,
  useCurrentScenarioId,
  useCurrentScenarioState,
} from '../../../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId, useWorkspaceScenarioExportConfig } from '../../../../../../state/hooks/WorkspaceHooks';
import { ConfigUtils } from '../../../../../../utils/ConfigUtils';

export const useScenarioExportButton = () => {
  const setApplicationErrorMessage = useSetApplicationErrorMessage();
  const currentScenarioData = useCurrentScenarioData();
  const currentScenarioId = useCurrentScenarioId();
  const currentScenarioState = useCurrentScenarioState();
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const scenarioExportConfig = useWorkspaceScenarioExportConfig();

  const showButton = useMemo(() => {
    if (currentScenarioState !== SCENARIO_RUN_STATE.SUCCESSFUL) return false;
    if (!ConfigUtils.isConfigValueTrueish(scenarioExportConfig?.enabled)) return false;

    const filePath = scenarioExportConfig?.filePath;
    if (filePath == null || filePath === '' || typeof filePath !== 'string') return false;

    return true;
  }, [currentScenarioState, scenarioExportConfig]);

  const downloadScenarioData = useCallback(
    async (event) => {
      event.stopPropagation();
      const filePathTemplate = scenarioExportConfig?.filePath ?? '';

      const filePath = filePathTemplate
        .replaceAll('<organizationId>', organizationId)
        .replaceAll('<workspaceId>', workspaceId)
        .replaceAll('<scenarioId>', currentScenarioId);

      const originalFileName = filePath.split('/').pop();
      let fileExtension = '';
      let baseFileName = originalFileName;
      const lastDotIndex = originalFileName.lastIndexOf('.');
      if (lastDotIndex > 0 && lastDotIndex < originalFileName.length - 1) {
        fileExtension = originalFileName.slice(lastDotIndex + 1);
        baseFileName = originalFileName.slice(0, lastDotIndex);
      }
      const scenarioName = (currentScenarioData?.name || 'scenario').replace(/[/\\:*?"<>|]/g, '');
      const currentDate = new Date().toISOString().split('T')[0];
      const downloadFileName = fileExtension
        ? `${baseFileName}_${scenarioName}_${currentDate}.${fileExtension}`
        : `${baseFileName}_${scenarioName}_${currentDate}`;

      try {
        await WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, filePath, downloadFileName);
      } catch (error) {
        let errorMessage = t(
          'commoncomponents.banner.scenarioExportFailed.unknown',
          'An unknown error occurred during the scenario data export. ' +
            'If the problem persists, please contact your administrator.'
        );

        try {
          const errorResponseText = await error?.response?.data.text();
          const errorDetails = JSON.parse(errorResponseText)?.detail;
          if (errorDetails === 'Status code 404, BlobNotFound') {
            errorMessage = t(
              'commoncomponents.banner.scenarioExportFailed.fileNotFound',
              'An error occurred during the scenario data export: file not found. ' +
                'If the problem persists, please contact your administrator.'
            );
          }
        } catch (_) {
          // Nothing to do, keep default error message
        }

        setApplicationErrorMessage(error, errorMessage);
      }
    },
    [
      organizationId,
      workspaceId,
      currentScenarioId,
      currentScenarioData?.name,
      scenarioExportConfig?.filePath,
      setApplicationErrorMessage,
    ]
  );

  return {
    showButton,
    downloadScenarioData,
  };
};
